import { useState } from 'react';
import { RankBadge } from './RankBadge';
import { SportBadge } from './SportBadge';
import { SkillLevelBadge, SkillLevel } from './SkillLevelBadge';
import { CoachBadge } from './CoachBadge';
import { FollowUserButton } from './FollowButton';
import { InviteToMatchDialog } from './InviteToMatchDialog';
import { MapPin, Trophy, Target, User as UserIcon, Star, UserPlus, Swords } from 'lucide-react';
import { useChallengePlayer } from '@/hooks/useChallengePlayer';
import { useCoachProfile } from '@/hooks/useCoachProfile';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import type { Database } from '@/integrations/supabase/types';
import type { Rank, Sport } from '@/types';

type Profile = Database['public']['Tables']['profiles']['Row'];

interface PlayerCardProps {
  player: Profile;
  onClick?: () => void;
  className?: string;
  showFollowButton?: boolean;
  showInviteButton?: boolean;
}

export function PlayerCard({ player, onClick, className, showFollowButton = true, showInviteButton = true }: PlayerCardProps) {
  const navigate = useNavigate();
  const { profile: currentUser } = useAuth();
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const { challengePlayer } = useChallengePlayer();
  const { data: coachProfile } = useCoachProfile(player.id);
  const isCoach = !!coachProfile;
  
  const winRate = player.matches_played > 0 
    ? Math.round((player.wins / player.matches_played) * 100) 
    : 0;

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(`/players/${player.id}`);
    }
  };

  const handleInviteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowInviteDialog(true);
  };

  const handleChallengeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    challengePlayer.mutate({
      challengedPlayerId: player.id,
      challengedPlayerName: player.name,
    });
  };

  const sports = (player.sports || []) as Sport[];
  const isEliteRank = player.rank === 'diamond' || player.rank === 'master';
  const canInvite = currentUser && currentUser.id !== player.id;

  return (
    <div
      onClick={handleClick}
      className={cn(
        'relative match-tile cursor-pointer group overflow-hidden',
        'active:scale-[0.98]',
        `rank-card-${player.rank}`,
        isEliteRank && `rank-glow-${player.rank}`,
        className
      )}
    >
      {/* Header Row - Name & Rank Left Aligned */}
      <div className="flex items-center justify-start gap-2 mb-2">
        <h3 className="text-sm font-bold text-foreground truncate">{player.name}</h3>
        {(player as any).player_id && (
          <span className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
            #{(player as any).player_id}
          </span>
        )}
        {isCoach && <CoachBadge isVerified={coachProfile?.is_verified ?? false} size="sm" />}
        <RankBadge rank={player.rank as Rank} size="sm" />
      </div>

      {/* Sub Header - Avatar & Actions */}
      <div className="flex items-start gap-3 mb-2">
        {/* Avatar with rank ring */}
        <div className="relative shrink-0">
          <div className={cn(
            "w-12 h-12 rounded-lg overflow-hidden transition-all bg-muted flex items-center justify-center",
            `avatar-ring-${player.rank}`
          )}>
            {player.avatar ? (
              <img
                src={player.avatar}
                alt={player.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <UserIcon className="w-6 h-6 text-muted-foreground" />
            )}
          </div>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-center gap-1.5 flex-wrap">
            {(player as any).skill_level && (
              <SkillLevelBadge level={(player as any).skill_level as SkillLevel} size="sm" />
            )}
            {/* Only show ranked sport */}
            {(player as any).ranked_sport && (
              <SportBadge sport={(player as any).ranked_sport as Sport} size="sm" />
            )}
          </div>

          {player.location && (
            <div className="flex items-center gap-1.5 text-xs">
              <MapPin className="w-3.5 h-3.5 text-accent shrink-0" />
              <span className="text-muted-foreground truncate">{player.location}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {canInvite && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 hover:bg-orange-500/10 hover:text-orange-500"
              onClick={handleChallengeClick}
              disabled={challengePlayer.isPending}
              title="Thách đấu"
            >
              <Swords className="w-3.5 h-3.5" />
            </Button>
          )}
          {showInviteButton && canInvite && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 hover:bg-accent/10 hover:text-accent"
              onClick={handleInviteClick}
              title="Mời tham gia trận đấu"
            >
              <UserPlus className="w-3.5 h-3.5" />
            </Button>
          )}
          {showFollowButton && (
            <FollowUserButton userId={player.id} size="sm" showLabel={false} />
          )}
        </div>
      </div>

      {/* Footer - Stats */}
      <div className="flex items-center justify-between pt-2 border-t border-accent/10">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-xs">
            <Trophy className="w-3.5 h-3.5 text-rank-gold" />
            <span className="text-foreground font-semibold">{player.wins}</span>
            <span className="text-muted-foreground">thắng</span>
          </div>
          <div className="flex items-center gap-1 text-xs">
            <Target className="w-3.5 h-3.5 text-accent" />
            <span className="text-foreground font-semibold">{winRate}%</span>
          </div>
          {(player as any).avg_rating > 0 && (
            <div className="flex items-center gap-0.5 text-xs">
              <Star className="w-3.5 h-3.5 text-rank-gold fill-rank-gold" />
              <span className="text-foreground font-semibold">
                {Number((player as any).avg_rating).toFixed(1)}
              </span>
            </div>
          )}
        </div>
        <span className="text-[10px] text-muted-foreground font-medium">
          {player.matches_played} trận
        </span>
      </div>

      {showInviteDialog && (
        <InviteToMatchDialog
          open={showInviteDialog}
          onOpenChange={setShowInviteDialog}
          inviteeId={player.id}
          inviteeName={player.name}
        />
      )}
    </div>
  );
}